import { Stack } from "@mantine/core"
import _ from "lodash"
import { useCallback, useMemo, Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import {
  CUSTOMISATION_CATEGORIES,
  CUSTOMISATION_CONFIGURATION_CATEGORIES,
  CustomisationCategory,
  CustomisationCategoryWithoutNone,
  CustomisationConfigurationCategory,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import { CategoryStylingParametersMapper } from "@tfg/types/schemas/communication-studio/stylingParameters"

import { useTranslationsMapper } from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/translationsMapper"
import {
  getConfigurationSchema,
  isConfigurationCategory,
} from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/utils"
import { useOrganizationSubscriptionPlanPermissions } from "~/pages/Brand/CommunicationStudio/contexts/organizationSubscriptionPlanPermissions/useOrganizationSubscriptionPlanPermissions"
import { ZodFormBuilder } from "~/utils/zod/zodFormBuilder"

import { useDesignTokenEditor } from "../contexts/designTokenEditor/useDesignTokenEditor"

type Props = {
  category: CustomisationCategoryWithoutNone
}

const useGetTooltipsConfig = () => {
  const { t } = useTranslation("communicationStudio")
  const tooltipsConfig = useMemo(() => {
    return {
      showProductReference: t("tabs.design.tooltips.showProductReference"),
      showCertificates: t("tabs.design.tooltips.showCertificates"),
      showProductImage: t("tabs.design.tooltips.showProductImage"),
      activitiesDepth: t("tabs.design.tooltips.activitiesDepth"),
    }
  }, [t])
  return tooltipsConfig
}

const useCommonDesignTokenFormProps = (
  category: CustomisationCategoryWithoutNone,
  isDirty: boolean,
  setIsDirty: Dispatch<SetStateAction<boolean>>,
  errors: Partial<Record<string, string>>,
  setErrors: Dispatch<SetStateAction<Partial<Record<string, string>>>>
) => {
  const { translationsMapper } = useTranslationsMapper()
  const { permissions: organizationPermissions } =
    useOrganizationSubscriptionPlanPermissions()

  const tooltipsConfig = useGetTooltipsConfig()

  const fetchKeyTranslation = useCallback(
    (path: string[]) => {
      const mapper = translationsMapper[category]
      const translation = _.get(mapper, path)
      if (!translation) {
        return path.join(".")
      }
      return translation
    },
    [translationsMapper, category]
  )

  const resolvePermission = useCallback(
    (key: string) => {
      const permission =
        organizationPermissions?.[SETTINGS_SCOPE.DESIGN_CUSTOMISATION]
      if (!permission) {
        return true
      }

      const stylingParameters =
        permission.authorizedDesignTokens?.stylingParameters
      const configurations = permission.authorizedDesignTokens?.configurations
      if (!stylingParameters || !configurations) {
        return false
      }

      if (isCategoryKey(category) && stylingParameters) {
        const stylingParametersPermission = getPermission(
          category,
          CUSTOMISATION_CATEGORIES,
          stylingParameters,
          key
        )

        if (!stylingParametersPermission) {
          const configurationPermission = getPermission(
            category,
            CUSTOMISATION_CONFIGURATION_CATEGORIES,
            configurations,
            key
          )

          return configurationPermission
        }
        return stylingParametersPermission
      }
      return false
    },
    [organizationPermissions, category]
  )
  return {
    isDirty,
    setIsDirty,
    fetchKeyTranslation,
    resolvePermission,
    tooltipsConfig,
    inlineLabel: true,
    errors,
    setErrors,
  }
}

function getPermission<T extends Record<string, Record<string, boolean>>>(
  category: string,
  categories: Record<string, string>,
  values: T | undefined,
  key: keyof T[keyof T] | string
): boolean {
  const categoryKey = Object.values(categories).find((c) => c === category)
  if (!categoryKey || !values) return false

  return values[categoryKey]?.[key] ?? false
}

export function getCategoryKeyFromValue(
  category: string
): CustomisationCategory | CustomisationConfigurationCategory | undefined {
  const categoryKey = Object.values(CUSTOMISATION_CATEGORIES).find(
    (c) => c === category
  )
  if (!categoryKey) {
    return undefined
  }
  return categoryKey
}

export function isConfigurationCategoryKey(
  key: string
): key is keyof typeof CUSTOMISATION_CONFIGURATION_CATEGORIES {
  return Object.values(CUSTOMISATION_CONFIGURATION_CATEGORIES).includes(
    key as keyof typeof CUSTOMISATION_CONFIGURATION_CATEGORIES
  )
}

export function isCategoryKey(
  key: string
): key is keyof typeof CUSTOMISATION_CATEGORIES {
  return Object.values(CUSTOMISATION_CATEGORIES).includes(
    key as keyof typeof CUSTOMISATION_CATEGORIES
  )
}

export const DesignTokensEditor = ({ category }: Props) => {
  const {
    styleData,
    setStyleData,
    configurations,
    setConfigurations,
    isDirty,
    setIsDirty,
    errors,
    setErrors,
  } = useDesignTokenEditor()
  const configurationSchema = getConfigurationSchema(category)

  const commonFormProps = useCommonDesignTokenFormProps(
    category,
    isDirty,
    setIsDirty,
    errors,
    setErrors
  )
  const stylingParametersContainCategory = (
    category: CustomisationCategoryWithoutNone
  ) => {
    const stylingParameters = CategoryStylingParametersMapper[category]
    return !!stylingParameters
  }

  return (
    <Stack gap="sm" miw="100%">
      {configurationSchema
        ? isConfigurationCategory(category) &&
          stylingParametersContainCategory(category) && (
            <ZodFormBuilder
              schema={configurationSchema}
              values={
                configurations[SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]?.[
                  category
                ] ?? {}
              }
              onChange={(value) => {
                setConfigurations((prevConfigurations) => ({
                  ...prevConfigurations,
                  [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: {
                    ...prevConfigurations[
                      SETTINGS_SCOPE.COMPONENT_CUSTOMISATION
                    ],
                    [category]: value,
                  },
                }))
              }}
              {...commonFormProps}
            />
          )
        : null}
      {styleData[category] && stylingParametersContainCategory(category) && (
        <ZodFormBuilder
          schema={CategoryStylingParametersMapper[category]}
          values={styleData[category]}
          onChange={(value) => {
            setStyleData((prevStyleData) => ({
              ...prevStyleData,
              [category]: value,
            }))
          }}
          {...commonFormProps}
        />
      )}
    </Stack>
  )
}
